import { useSuspenseQuery } from '@tanstack/react-query';
import { parseJSON } from 'date-fns/fp';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { maxNetworksQueryOptions } from '#api/networks.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { SelectNetwork } from '#components/patterns/entity-select/network/SelectNetwork.tsx';
import { NO_ROLE_FLAG, SelectRole } from '#components/patterns/entity-select/role/index.ts';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ComboboxTags } from '#components/patterns/forms/select/combobox/combobox-tags/ComboboxTags.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { Select } from '#components/patterns/forms/select/select/Select.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useTime } from '#hooks/useTime.ts';
import {
  buildExpiresAt,
  DEFAULT_EXPIRATION,
  expirationOptions,
  type ExpirationPreset,
} from '#screens/settings/api-keys/form/expiration.ts';
import type { NewKeyState } from '#screens/settings/api-keys/types.ts';
import { getCidrV4, getCidrV6 } from '#util/networkCidrs.ts';
import { formatISODay } from '#util/time.ts';
import { checkIsFormValid } from '#util/validation/checkIsFormValid.ts';
import { useAddJoinKey, useEditJoinKey } from './JoinKeyForm.queries.ts';

type NewJoinKeyProps = {
  id: null;
  /** Send the new key's token to the parent, which shows it once */
  onSuccess: (newKey: NewKeyState) => void;
};

type EditJoinKeyProps = {
  id: string;
  /** Only the name and expiry can change; the host shape is immutable */
  defaultValues: {
    name: string;
    expiresAt: string | null;
  };
  onSuccess: () => void;
};

type Props = NewJoinKeyProps | EditJoinKeyProps;

type FormData = {
  name: string;
  networkID: string;
  /** The role enrolled hosts get, or NO_ROLE_FLAG */
  roleID: string;
  tags: string[];
  /** Only offered on dual-stack networks; IPv4-only networks always assign one */
  autoAssignIPv4: boolean;
  expiration: ExpirationPreset;
  /** ISO day (yyyy-MM-dd) from the date input, only used when expiration is 'custom' */
  customExpirationDate: string;
};

// The name becomes part of enrolled hosts' names, so the API requires a DNS label short enough
// to leave room for the random suffix it appends.
export const MAX_NAME_LENGTH = 54;
const NAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const NAME_PATTERN_MESSAGE = 'Use lowercase letters, digits and hyphens, and don’t start or end with a hyphen';

/**
 * A form that adds join keys, and edits the name and expiry of existing ones.
 */
export function JoinKeyForm(props: Props) {
  const { id, onSuccess } = props;
  const isAddingNewKey = id === null;
  const {
    data: { data: networks },
  } = useSuspenseQuery(maxNetworksQueryOptions);

  const defaultValues: FormData =
    isAddingNewKey ?
      {
        name: '',
        networkID: networks.length === 1 ? (networks[0]?.id ?? '') : '',
        roleID: NO_ROLE_FLAG,
        tags: [],
        // Matches host creation, which defaults to assigning an IPv4 address
        autoAssignIPv4: true,
        expiration: DEFAULT_EXPIRATION,
        customExpirationDate: '',
      }
    : {
        name: props.defaultValues.name,
        networkID: '',
        roleID: NO_ROLE_FLAG,
        tags: [],
        autoAssignIPv4: false,
        // An existing expiry can only be represented as a custom date
        expiration: props.defaultValues.expiresAt ? 'custom' : 'never',
        customExpirationDate:
          props.defaultValues.expiresAt ? formatISODay(parseJSON(props.defaultValues.expiresAt)) : '',
      };

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty, isSubmitting, isSubmitted, dirtyFields },
  } = useForm<FormData>({ mode: 'onChange', defaultValues });
  const isValid = checkIsFormValid(errors);
  const addJoinKey = useAddJoinKey();
  const editJoinKey = useEditJoinKey();
  const today = formatISODay(useTime());

  const [expiration, networkID] = useWatch({ control, name: ['expiration', 'networkID'] });
  const selectedNetwork = networks.find((n) => n.id === networkID);
  // IPv4 is assigned unconditionally on IPv4-only networks, so the choice only exists on dual-stack ones
  const showAutoAssignIPv4 =
    selectedNetwork ? Boolean(getCidrV4(selectedNetwork) && getCidrV6(selectedNetwork)) : false;

  const onSubmit = handleSubmit(async (data) => {
    // Checked here rather than as a field rule: the select reports its initial value as a change, which
    // under live validation would flag the empty field before the user has touched the form
    if (isAddingNewKey && !data.networkID) {
      setError('networkID', { message: 'A network must be selected' });
      return;
    }
    try {
      if (isAddingNewKey) {
        const v4 = selectedNetwork ? getCidrV4(selectedNetwork) : undefined;
        const joinKey = await addJoinKey.mutateAsync({
          name: data.name,
          networkID: data.networkID,
          roleID: data.roleID === NO_ROLE_FLAG ? undefined : data.roleID,
          tags: data.tags,
          // The API reads an IPv4 prefix as the signal to assign one; omitted, it assigns IPv6 only on a dual-stack network
          autoAssignCIDRs: showAutoAssignIPv4 && data.autoAssignIPv4 && v4 ? [v4] : undefined,
          expiresAt: buildExpiresAt(data.expiration, data.customExpirationDate),
        });
        onSuccess({ token: joinKey.token, name: joinKey.name });
      } else {
        // Resending an untouched custom date would move the expiry to the end of that day, so only
        // send expiresAt when the user changed it.  null removes the expiry.
        const expiryChanged = Boolean(dirtyFields.expiration ?? dirtyFields.customExpirationDate);
        await editJoinKey.mutateAsync({
          id,
          values: {
            name: data.name,
            ...(expiryChanged && { expiresAt: buildExpiresAt(data.expiration, data.customExpirationDate) ?? null }),
          },
        });
        onSuccess();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          // The API reports list positions (`tags[0]`, `autoAssignCIDRs[0]`); the form has one control per list
          if (path && /^tags(\[\d+\])?$/.test(path)) {
            setError('tags', { message }, { shouldFocus: true });
            return true;
          }
          if (path && /^autoAssignCIDRs(\[\d+\])?$/.test(path)) {
            setError('autoAssignIPv4', { message }, { shouldFocus: true });
            return true;
          }
          switch (path) {
            case 'name':
            case 'networkID':
            case 'roleID':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case 'expiresAt':
              if (data.expiration === 'custom') {
                setError('customExpirationDate', { message }, { shouldFocus: true });
              } else {
                setError('expiration', { message }, { shouldFocus: true });
              }
              return true;
            case undefined: // e.g. the org hit the join key limit
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        trackError(error);
        setError('root.global', { message: 'We couldn’t save the join key, please try again' });
      }
    }
  });

  const globalError = errors.root?.global?.message ?? 'Unable to save.  Please update and try again.';

  return (
    <form onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide" className="mb-40">
        <Field label="Name">
          <HelpText>
            Hosts enrolled with this key are named after the machine, falling back to this name. Lowercase letters,
            digits and hyphens only.
          </HelpText>
          <Input
            className="width-100"
            hasError={Boolean(errors.name)}
            autoComplete="off"
            maxLength={MAX_NAME_LENGTH}
            {...register('name', {
              required: 'A name for the join key must be provided',
              pattern: { value: NAME_PATTERN, message: NAME_PATTERN_MESSAGE },
            })}
          />
          {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
        </Field>
        {isAddingNewKey && (
          <>
            <Spacer size="wide" stackWhenNarrow={true}>
              <SelectNetwork
                label="Network"
                name="networkID"
                control={control}
                className="width-100"
                helpTip="Hosts enrolled with this key join this network.  It can’t be changed after the key is created."
                errorMessage={errors.networkID?.message}
              />
              <SelectRole
                label="Role"
                name="roleID"
                control={control}
                className="width-100"
                helpTip="Hosts enrolled with this key get this role.  It can’t be changed after the key is created."
                errorMessage={errors.roleID?.message}
              />
            </Spacer>
            {showAutoAssignIPv4 && (
              <Field>
                <Checkbox
                  {...register('autoAssignIPv4')}
                  hasError={Boolean(errors.autoAssignIPv4)}
                  label="Also assign an IPv4 address to enrolled hosts"
                />
                {errors.autoAssignIPv4?.message && (
                  <ErrorMessage className="mb-8">{errors.autoAssignIPv4.message}</ErrorMessage>
                )}
              </Field>
            )}
            <div>
              <ComboboxTags
                name="tags"
                control={control}
                hasError={Boolean(errors.tags)}
                helpText="Hosts enrolled with this key get these tags.  They can’t be changed after the key is created."
              />
              {errors.tags && <ErrorMessage>{errors.tags.message}</ErrorMessage>}
            </div>
          </>
        )}
        <Controller
          control={control}
          name="expiration"
          render={({ field }) => (
            <Select<ExpirationPreset>
              label="Expiration"
              defaultValue={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              innerRef={field.ref}
              help={
                <HelpText>
                  After this, the key can no longer enroll hosts. Hosts already enrolled stay enrolled.
                </HelpText>
              }
              renderValue={(value) => expirationOptions.find((option) => option.value === value)?.label ?? null}
              errorMessage={errors.expiration?.message}
            >
              {expirationOptions.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </Select>
          )}
        />
        {expiration === 'custom' && (
          <Field label="Expiration date">
            <Input
              className="width-100"
              type="date"
              min={today}
              hasError={Boolean(errors.customExpirationDate)}
              {...register('customExpirationDate', {
                validate: (value, formValues) => {
                  if (formValues.expiration !== 'custom') {
                    return true;
                  }
                  if (!value) {
                    return 'An expiration date must be provided';
                  }
                  if (value < today) {
                    return 'The expiration date must be in the future';
                  }
                  return true;
                },
              })}
            />
            {errors.customExpirationDate && <ErrorMessage>{errors.customExpirationDate.message}</ErrorMessage>}
          </Field>
        )}
      </Spacer>
      <ConfirmChangesBanner
        isOpen={isDirty}
        title={isSubmitted && !isValid ? globalError : undefined}
        primaryButton={
          <Button variant="primary" type="submit" disabled={(!isAddingNewKey && !isDirty) || isSubmitting}>
            Save
          </Button>
        }
        secondaryButton={<CancelLinkButton fallbackTo="/settings/join-keys" />}
      />
    </form>
  );
}
